import React from 'react';
import classNames from 'classnames';
import { NavLink, useLocation, useHistory } from 'react-router-dom';

import { AlertIcon, SelectMenu, Tooltip } from 'components';
import Icon from 'assets/icons';

import { oxfordify } from 'helpers/string';
import { useTranslation } from 'hooks/useTranslation';

import styles from './VerticalListItem.module.scss';

export interface VerticalListItemProps {
  /** @string - List item name */
  name: string;
  /** @string - List item type. Can be "activity-feed", "profile", "manage-apps" or "integration" */
  type?: 'activity-feed' | 'profile' | 'integration' | 'my-docs';
  /** @string - Passing "Simple" here creates the mobile view */
  navigationType?: string;
  /** @ReactNode - List item icon */
  icon?: React.ReactNode;
  /** @User - User data */
  user?: User;
  /** @Integration - Integration data */
  integrations?: UserIntegration[];
  /** @any - Tracking event function */
  trackingEvent?: any;
  /** @function */
  connectIntegration?: (integration: UserIntegration) => void;
}

export const VerticalListItem: React.FC<VerticalListItemProps> = ({
  name,
  icon,
  type,
  navigationType,
  user,
  integrations,
  trackingEvent,
  connectIntegration,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const linkClasses = classNames(
    styles.navItem,
    {
      [styles.navUserLink]: type === 'profile' || type === 'activity-feed',
    },
    styles[`type--${navigationType}`]
  );

  // If all the integrations are broken
  const allIntegrationsBroken = (integrations || []).every((integration) => integration.broken);

  // Show broken icon if at least one integration is broken
  const showBrokenIcon = (integrations || []).some((integration) => integration.broken);

  const containerClasses = classNames(styles.navItemContainer, styles[`type--${navigationType}`], {
    [styles.isDisconnected]: allIntegrationsBroken && type === 'integration',
    [styles.navUserLink]: type === 'profile' || type === 'activity-feed',
  });

  /**
   * Calls a method on the parent to start reconnecting an existing integration
   */
  const handleReconnectIntegration = () => {
    const brokenIntegrations = getBrokenIntegrations();

    if (connectIntegration) {
      /**
       * If only one integration of the type is broken it should
       * open the auth url for the integration
       */
      if (brokenIntegrations?.length === 1) {
        connectIntegration(brokenIntegrations[0]);
      }

      /**
       * If more than one integration of the type is broken
       * it should take the user to the manage apps screen
       */
      if (brokenIntegrations?.length > 1) {
        history.push('/manage-apps');
      }
    }
  };

  /**
   * Calls a method on the parent to start connecting a new integration
   */
  const handleConnectIntegration = React.useCallback(() => {
    if (connectIntegration && integrations) {
      connectIntegration(integrations[0]);
    }
  }, [integrations, connectIntegration]);

  /**
   * Generates the select menu click actions
   * according to the item type
   */
  const getActions = React.useCallback(() => {
    switch (type) {
      case 'activity-feed':
        return [
          {
            key: 'manage_apps',
            value: t('vertical-navigation.item.actions.manage-apps'),
            props: {
              to: '/manage-apps',
            },
          },
        ];

      case 'integration':
        return [
          {
            key: 'add_account',
            value: t('vertical-navigation.item.actions.add-account'),
            onClick: handleConnectIntegration,
          },
        ];

      default:
        return undefined;
    }
    /** eslin */
  }, [t, type, handleConnectIntegration]);

  const getLink = React.useCallback(() => {
    switch (type) {
      case 'profile':
        return `/profile/${user?.id}`;
      case 'my-docs':
        return `/profile/${user?.id}?owned=true`;
      case 'activity-feed':
        return '/';
      case 'integration':
        return `/integration/${integrations![0]!.type}`;
      default:
        return '';
    }
  }, [user, type, integrations]);

  const itemActions = getActions();

  const isCurrentRoute = () => {
    // navlink uses to={} to determine active link
    // we can change 'activity feed' route from '/' to '/home' to avoid this, as '/' is a catchall
    // but we want to keep the / so we have to manually override  the control flow of navlink
    const homeRouteMatch = type === 'activity-feed' && location.pathname === '/';
    const integrationRouteMatch =
      type === 'integration' && location.pathname.indexOf('integration') > -1;
    if (homeRouteMatch || integrationRouteMatch) {
      return styles.active;
    } else return '';
  };

  //@ts-ignore
  const iconWithAppliedClass = React.cloneElement(icon, {
    className: classNames(styles.icon),
  });

  const getBrokenTooltipText = () => {
    const brokenIntegrations = getBrokenIntegrations();

    /**
     * If only one integration of the type is broken
     * Tooltip should be: integration.title not connected, click to connect
     */
    if (brokenIntegrations.length === 1) {
      return `${brokenIntegrations![0].integrationUserId} ${t(
        'vertical-navigation.item.tooltip.not-connected'
      )}`;
    }

    /**
     * If more than one integration of the type is broken
     * Tooltip should be: integration.title and integration.title not connected, click to connect
     */
    if (brokenIntegrations.length > 1) {
      const integrationUserIds = brokenIntegrations.map(
        (integration) => integration.integrationUserId!
      );
      return `${oxfordify(integrationUserIds)} ${t(
        'vertical-navigation.item.tooltip.not-connected'
      )}`;
    }
  };

  /**
   * Returns the integrations that are broken
   */
  const getBrokenIntegrations = (): UserIntegration[] => {
    return (integrations || []).filter((integration) => {
      return integration.broken;
    });
  };

  return (
    <div className={containerClasses}>
      <NavLink
        className={linkClasses}
        activeClassName={isCurrentRoute()}
        to={getLink()}
        onClick={trackingEvent}
      >
        <div className={styles.navTitle}>
          {iconWithAppliedClass}
          {name}
        </div>
      </NavLink>

      {itemActions && (
        <div className={styles.actions}>
          <SelectMenu
            closeOnClick
            buttonActiveClass={styles.actionsActive}
            menuClassName={styles.actionsMenu}
            id={!!integrations?.length ? integrations[0].id : type || 'sidebar'}
            arrow={false}
            label={<Icon.Actions />}
            horizontalPosition="Right"
            options={itemActions}
            onChange={handleConnectIntegration}
          />
        </div>
      )}

      {integrations && showBrokenIcon && (
        <Tooltip
          content={getBrokenTooltipText()}
          position="AboveRight"
          className={classNames(
            styles.disconnectedIconContainer,
            styles[`type--${navigationType}`]
          )}
          bump
        >
          <AlertIcon onClick={handleReconnectIntegration} className={styles.disconnectedIcon} />
        </Tooltip>
      )}
    </div>
  );
};
